import React from "react";
import { Box, FormControl, FormLabel, Option } from "@mui/joy";
import DarkInput from "components/common/DarkInput";
import DarkSelect from "components/common/DarkSelect";
import SearchInputWithDropdown from "components/common/SearchInputWithDropdown";
import type { SearchResultItem } from "components/common/SearchInputWithDropdown";
import IconButtonCTA from "components/common/IconButtonCTA";
import PersonChip from "components/common/PersonChip";
import { hrAccentColor, textTokens } from "styles/globalStyles";
import { SCreate, SClose } from "styles/StyledMUI";
import {
  normalizeParamType,
  type ParamValueType,
} from "../../../excursions/template/display/paramsTemplateUtils";
import { bookingStatusChipForStaffParamPicker } from "../../../excursions/shared/utils/excursionUtils";

/** Scalar template types whose name must not appear as the field value in inputs. */
function displayValueForScalarParam(type: string, raw: unknown): string {
  if (raw == null || raw === "") return "";
  const s = String(raw);
  const t = type.trim().toLowerCase();
  if (["string", "number", "boolean"].includes(t) && s.trim().toLowerCase() === t) return "";
  return s;
}

function getSearchArray(v: unknown): SearchResultItem[] {
  if (Array.isArray(v)) {
    return v.filter(
      (item): item is SearchResultItem =>
        item != null &&
        typeof item === "object" &&
        "id" in item &&
        "label" in item &&
        (item as SearchResultItem).id != null,
    );
  }
  if (v && typeof v === "object" && "id" in v && "label" in v) {
    const o = v as SearchResultItem;
    return o.id != null ? [o] : [];
  }
  return [];
}

const chipSx = {
  backgroundColor: "#444",
  color: textTokens.color.primary,
  maxWidth: "100%",
  "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
};

function personSearchFnForType(
  pickerType: ParamValueType,
  searchUsers: ParamsFieldsRendererProps["searchUsers"],
  searchCustomers: ParamsFieldsRendererProps["searchCustomers"],
  searchGuides: ParamsFieldsRendererProps["searchGuides"],
): ((query: string) => Promise<SearchResultItem[]>) | undefined {
  switch (pickerType) {
    case "users":
      return searchUsers;
    case "customers":
      return searchCustomers;
    case "guides":
      return searchGuides;
    default:
      return undefined;
  }
}

function personParamPlaceholder(pickerType: ParamValueType): string {
  switch (pickerType) {
    case "users":
      return "Rechercher un utilisateur…";
    case "customers":
      return "Rechercher un client…";
    case "guides":
      return "Rechercher un encadrant…";
    default:
      return "";
  }
}

function personParamAriaAdd(pickerType: ParamValueType): string {
  switch (pickerType) {
    case "users":
      return "Ajouter un utilisateur";
    case "customers":
      return "Ajouter un client";
    case "guides":
      return "Ajouter un encadrant";
    default:
      return "Ajouter";
  }
}

export type ParamsFieldsRendererProps = {
  paramsTemplate: Record<string, string>;
  params: Record<string, unknown>;
  onChange: (params: Record<string, unknown>) => void;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  labelFontSize?: string;
  gap?: number;
  searchUsers?: (query: string) => Promise<SearchResultItem[]>;
  searchCustomers?: (query: string) => Promise<SearchResultItem[]>;
  searchGuides?: (query: string) => Promise<SearchResultItem[]>;
};

export function ParamsFieldsRenderer({
  paramsTemplate,
  params,
  onChange,
  disabled = false,
  size = "sm",
  labelFontSize,
  gap = 1,
  searchUsers,
  searchCustomers,
  searchGuides,
}: ParamsFieldsRendererProps) {
  const [activeSearchKey, setActiveSearchKey] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchResults, setSearchResults] = React.useState<SearchResultItem[]>([]);

  React.useEffect(() => {
    if (!activeSearchKey || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const typeRaw = paramsTemplate[activeSearchKey];
    const pickerType = normalizeParamType(typeRaw);

    const search = personSearchFnForType(pickerType, searchUsers, searchCustomers, searchGuides);
    if (!search) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    search(searchQuery).then((res) => {
      if (!cancelled) setSearchResults(res ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [activeSearchKey, searchQuery, paramsTemplate, searchUsers, searchCustomers, searchGuides]);

  if (!paramsTemplate || Object.keys(paramsTemplate).length === 0) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap }}>
      {Object.entries(paramsTemplate).map(([key, typeRaw]) => {
        const type = normalizeParamType(typeRaw);

        return (
          <FormControl key={key} sx={{ minWidth: 0 }}>
            <FormLabel sx={{ color: hrAccentColor, fontSize: labelFontSize }}>{key}</FormLabel>
            {type === "boolean" ? (
              <DarkSelect
                value={params[key] === true ? "true" : "false"}
                onChange={(_, v) => v != null && onChange({ ...params, [key]: v === "true" })}
                disabled={disabled}
                size={size}
                sx={{ minWidth: 0 }}
              >
                <Option value="false">Non</Option>
                <Option value="true">Oui</Option>
              </DarkSelect>
            ) : type === "number" ? (
              <DarkInput
                type="number"
                size={size}
                value={displayValueForScalarParam(type, params[key])}
                onChange={(e) => {
                  const v = e.target.value;
                  const n = v === "" ? undefined : parseFloat(v);
                  onChange({ ...params, [key]: n });
                }}
                disabled={disabled}
                slotProps={{ input: { sx: { color: textTokens.color.primary }, min: 0 } }}
              />
            ) : type === "users" || type === "customers" || type === "guides" ? (
              personSearchFnForType(type, searchUsers, searchCustomers, searchGuides) ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
                  {getSearchArray(params[key]).map((item, idx) => (
                    <PersonChip
                      key={`${item.id}-${idx}`}
                      label={item.label}
                      bookingStatus={bookingStatusChipForStaffParamPicker(type, item)}
                      variant="soft"
                      size={size}
                      chipColor="#555"
                      onRemove={
                        disabled
                          ? undefined
                          : () => {
                              const arr = getSearchArray(params[key]);
                              onChange({ ...params, [key]: arr.filter((_, i) => i !== idx) });
                            }
                      }
                      confirmRemove={
                        disabled
                          ? undefined
                          : { title: "Retirer", message: "Retirer cet élément ?" }
                      }
                      sx={chipSx}
                    />
                  ))}
                  {!disabled && activeSearchKey === key ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        flex: { xs: "1 1 100%", sm: "1 1 140px" },
                        minWidth: 0,
                      }}
                    >
                      <SearchInputWithDropdown
                        value={searchQuery}
                        onChange={(val) => setSearchQuery(val)}
                        onFocus={() => setActiveSearchKey(key)}
                        onBlur={() => {
                          setActiveSearchKey(null);
                          setSearchQuery("");
                        }}
                        placeholder={personParamPlaceholder(type)}
                        results={searchResults.filter(
                          (r) => !getSearchArray(params[key]).some((s) => s.id === r.id),
                        )}
                        onSelect={(item) => {
                          if (item.disabled) return;
                          const arr = getSearchArray(params[key]);
                          if (arr.some((s) => s.id === item.id)) return;
                          onChange({ ...params, [key]: [...arr, item] });
                          setSearchQuery("");
                        }}
                        disabled={disabled}
                        size={size}
                      />
                      <IconButtonCTA
                        size={size}
                        variant="plain"
                        aria-label="Fermer la recherche"
                        onClick={() => {
                          setActiveSearchKey(null);
                          setSearchQuery("");
                        }}
                      >
                        <SClose />
                      </IconButtonCTA>
                    </Box>
                  ) : !disabled ? (
                    <IconButtonCTA
                      size={size}
                      variant="soft"
                      aria-label={personParamAriaAdd(type)}
                      ctaVariant="accent"
                      onClick={() => setActiveSearchKey(key)}
                      sx={{ flexShrink: 0 }}
                    >
                      <SCreate />
                    </IconButtonCTA>
                  ) : null}
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
                  {getSearchArray(params[key]).length === 0 ? (
                    <Box
                      component="span"
                      sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.875rem" }}
                    >
                      —
                    </Box>
                  ) : (
                    getSearchArray(params[key]).map((item, idx) => (
                      <PersonChip
                        key={`${item.id}-${idx}`}
                        label={item.label}
                        bookingStatus={bookingStatusChipForStaffParamPicker(type, item)}
                        variant="soft"
                        size={size}
                        chipColor="#555"
                        sx={chipSx}
                      />
                    ))
                  )}
                </Box>
              )
            ) : (
              <DarkInput
                size={size}
                value={displayValueForScalarParam(type, params[key])}
                onChange={(e) => onChange({ ...params, [key]: e.target.value })}
                disabled={disabled}
                slotProps={{ input: { sx: { color: textTokens.color.primary } } }}
              />
            )}
          </FormControl>
        );
      })}
    </Box>
  );
}
